import { Lead } from "@/types/lead";
import { toast } from "@/hooks/use-toast";
import { importPlaceAsLead } from "@/services/places/importService";
import { PlaceResult } from "@/types/place";
import { parseCSVToLeads } from "./parserService";
import { searchPlaces } from "@/services/places/searchService";
import { checkUserLeadQuota } from "@/services/subscriptions";
import { supabase } from "@/integrations/supabase/client";

interface ImportResults {
  success: number;
  skipped: number;
  failed: number;
  errors: string[];
}

const INTER_REQUEST_DELAY = 1500;
const RATE_LIMIT_COOLDOWN = 10000;
const EXTENDED_COOLDOWN = 30000;
const MAX_CONSECUTIVE_RATE_LIMITS = 5;

type PlaceMatchResult = { place: PlaceResult } | { rateLimited: true } | { notFound: true };

async function findBestPlaceMatch(lead: Partial<Lead>): Promise<PlaceMatchResult> {
  try {
    const searchQuery = `${lead.name || ''} ${lead.address || ''}`.trim();
    
    if (!searchQuery) {
      console.error('Cannot search for place: No name or address provided in CSV data');
      return { notFound: true };
    }
    
    console.log(`Searching for place: "${searchQuery}"`);
    
    // skipRetryOnRateLimit: let the import loop handle cooldowns
    const response = await searchPlaces(searchQuery, '', 10000, true, { skipRetryOnRateLimit: true });
    
    if (response.status === 'OVER_QUERY_LIMIT') {
      console.warn('Rate limited by Google Places for:', searchQuery);
      return { rateLimited: true };
    }
    
    if (response.status !== 'OK' || !response.results?.length) {
      console.error('No place found for:', searchQuery);
      return { notFound: true };
    }
    
    const bestMatch = response.results[0];
    console.log('Found place match:', bestMatch.name, 'with address:', bestMatch.formatted_address);
    
    return { place: bestMatch };
  } catch (error) {
    console.error('Error searching for place match:', error);
    return { notFound: true };
  }
}

export interface PreviewResult {
  total: number;
  newLeads: number;
  duplicates: number;
  newLeadNames: string[];
  duplicateNames: string[];
}

async function fetchExistingLeadNames(): Promise<Set<string>> {
  const { data: { user } } = await supabase.auth.getUser();
  const { data: profile } = await supabase
    .from('profiles').select('company_id').eq('id', user!.id).single();

  const existingNames = new Set<string>();
  if (profile?.company_id) {
    let from = 0;
    const PAGE_SIZE = 1000;
    while (true) {
      const { data } = await supabase
        .from('leads')
        .select('name')
        .eq('company_id', profile.company_id)
        .range(from, from + PAGE_SIZE - 1);
      if (!data?.length) break;
      data.forEach(l => existingNames.add(l.name?.toLowerCase().trim()));
      if (data.length < PAGE_SIZE) break;
      from += PAGE_SIZE;
    }
  }
  return existingNames;
}

export const previewCSVImport = async (csvContent: string): Promise<PreviewResult> => {
  const leads = parseCSVToLeads(csvContent);
  if (!leads.length) {
    return { total: 0, newLeads: 0, duplicates: 0, newLeadNames: [], duplicateNames: [] };
  }

  const existingNames = await fetchExistingLeadNames();
  const newLeadNames: string[] = [];
  const duplicateNames: string[] = [];

  for (const lead of leads) {
    const normalized = lead.name?.toLowerCase().trim();
    if (normalized && existingNames.has(normalized)) {
      duplicateNames.push(lead.name || 'Unnamed');
    } else {
      newLeadNames.push(lead.name || 'Unnamed');
    }
  }

  return {
    total: leads.length,
    newLeads: newLeadNames.length,
    duplicates: duplicateNames.length,
    newLeadNames,
    duplicateNames,
  };
};

export const importLeadsFromCSV = async (
  csvContent: string,
  onProgress?: (current: number, total: number) => void,
  signal?: AbortSignal
): Promise<ImportResults> => {
  try {
    const leads = parseCSVToLeads(csvContent);
    
    if (!leads.length) {
      return { success: 0, skipped: 0, failed: 0, errors: ["No valid leads found in CSV file"] };
    }

    const hasQuota = await checkUserLeadQuota();
    if (!hasQuota) {
      return {
        success: 0, skipped: 0, failed: leads.length,
        errors: ["You've reached your monthly lead import limit. Upgrade your subscription for more imports."]
      };
    }
    
    // Pre-filter: fetch existing lead names to skip duplicates before Google Places calls
    const { data: { user } } = await supabase.auth.getUser();
    const { data: profile } = await supabase
      .from('profiles').select('company_id').eq('id', user!.id).single();

    const existingNames = await fetchExistingLeadNames();
    console.log(`Pre-filter: loaded ${existingNames.size} existing lead names`);

    let successCount = 0;
    let skippedCount = 0;
    let preFilteredCount = 0;
    const failedLeads: string[] = [];
    const total = leads.length;
    let quotaExhausted = false;
    let consecutiveRateLimits = 0;
    
    for (let i = 0; i < leads.length; i++) {
      // Check for cancellation
      if (signal?.aborted) {
        failedLeads.push(`Import cancelled. ${total - i} remaining lead(s) were skipped.`);
        break;
      }

      const lead = leads[i];
      if (onProgress) {
        onProgress(i, total);
      }

      // Pre-filter by name before making any API calls
      const normalizedName = lead.name?.toLowerCase().trim();
      if (normalizedName && existingNames.has(normalizedName)) {
        preFilteredCount++;
        console.log(`Pre-filtered duplicate by name: "${lead.name}"`);
        continue;
      }

      if (i > 0) {
        await new Promise(res => setTimeout(res, INTER_REQUEST_DELAY));
      }
      
      // Re-check abort after delay
      if (signal?.aborted) {
        failedLeads.push(`Import cancelled. ${total - i} remaining lead(s) were skipped.`);
        break;
      }

      try {
        const matchResult = await findBestPlaceMatch(lead);
        
        // Handle rate limiting with progressive cooldowns
        if ('rateLimited' in matchResult) {
          consecutiveRateLimits++;
          console.warn(`Rate limit hit ${consecutiveRateLimits}/${MAX_CONSECUTIVE_RATE_LIMITS}`);
          
          if (consecutiveRateLimits >= MAX_CONSECUTIVE_RATE_LIMITS) {
            failedLeads.push(`Google Places rate limit reached after ${consecutiveRateLimits} consecutive failures. ${total - i} remaining lead(s) were skipped.`);
            break;
          }
          
          const cooldown = consecutiveRateLimits >= 3 ? EXTENDED_COOLDOWN : RATE_LIMIT_COOLDOWN;
          console.log(`Applying ${cooldown / 1000}s cooldown before retrying...`);
          await new Promise(res => setTimeout(res, cooldown));
          
          if (signal?.aborted) {
            failedLeads.push(`Import cancelled during cooldown. ${total - i} remaining lead(s) were skipped.`);
            break;
          }
          
          // Retry this lead once after cooldown
          const retryResult = await findBestPlaceMatch(lead);
          if ('rateLimited' in retryResult || 'notFound' in retryResult) {
            failedLeads.push(`Failed to import lead "${lead.name || 'Unnamed'}": Rate limited by Google Places`);
            continue;
          }
          consecutiveRateLimits = 0;
          var placeMatch = retryResult.place;
        } else if ('notFound' in matchResult) {
          failedLeads.push(`No Google Places match found for "${lead.name || 'Unnamed lead'}"`);
          consecutiveRateLimits = 0;
          continue;
        } else {
          consecutiveRateLimits = 0;
          var placeMatch = matchResult.place;
        }
        
        const enhancedPlace: PlaceResult = {
          ...placeMatch,
          name: lead.name || placeMatch.name,
          international_phone_number: lead.phone || placeMatch.international_phone_number,
          website: lead.website || placeMatch.website,
        };
        
        if (lead.latitude && lead.longitude) {
          enhancedPlace.geometry = {
            location: {
              lat: typeof lead.latitude === 'number' ? lead.latitude : 
                   typeof lead.latitude === 'string' ? Number(lead.latitude) : placeMatch.geometry.location.lat,
              lng: typeof lead.longitude === 'number' ? lead.longitude : 
                   typeof lead.longitude === 'string' ? Number(lead.longitude) : placeMatch.geometry.location.lng
            }
          };
        }

        console.log('Importing place with place_id:', enhancedPlace.place_id);
        const importResult: any = await importPlaceAsLead(enhancedPlace, lead.category);
        
        if (importResult === 'duplicate') {
          skippedCount++;
          console.log(`Skipped duplicate: "${lead.name || 'Unnamed'}"`);
        } else if (importResult) {
          successCount++;
        } else {
          quotaExhausted = true;
          const remaining = total - i;
          failedLeads.push(`Import limit reached. ${remaining} remaining lead(s) were skipped.`);
          break;
        }
      } catch (error) {
        console.error('Error importing lead:', error);
        const msg = (error as Error).message || 'Unknown error';
        
        if (msg.includes('quota') || msg.includes('limit') || msg.includes('Import Limit')) {
          quotaExhausted = true;
          const remaining = total - i;
          failedLeads.push(`Import limit reached. ${remaining} remaining lead(s) were skipped.`);
          break;
        }
        
        failedLeads.push(`Failed to import lead "${lead.name || 'Unnamed'}": ${msg}`);
      }
    }
    
    const totalSkipped = skippedCount + preFilteredCount;
    const preFilterNote = preFilteredCount > 0 ? ` (${preFilteredCount} by name match)` : '';
    
    if (quotaExhausted && successCount > 0) {
      toast({
        title: "Partial Import",
        description: `Imported ${successCount}, skipped ${totalSkipped} duplicates${preFilterNote} before reaching your import limit.`,
      });
    } else if (totalSkipped > 0 || failedLeads.length > 0) {
      toast({
        title: "Import Complete",
        description: `Imported ${successCount}, skipped ${totalSkipped} duplicate(s)${preFilterNote}, ${failedLeads.length} failed.`,
      });
    }
    
    return {
      success: successCount,
      skipped: totalSkipped,
      failed: failedLeads.length,
      errors: failedLeads
    };
  } catch (error) {
    console.error('Error in importLeadsFromCSV:', error);
    throw error;
  }
};
